/**
 * Map the free-text niches in a file ("Football Edits", "Lip Sync") onto the
 * team's niche list. The outreacher reviews every guess before anything is
 * sent; this only saves them picking the obvious ones by hand.
 */
import { isEmptyAnswer } from './columns';

export interface NicheOption {
  code: string;
  displayName: string;
}

/** Words that describe the format of the account, not its niche. */
const STOPWORDS = new Set([
  'edit', 'edits', 'editz', 'editing', 'editor', 'content', 'video', 'videos', 'clip', 'clips', 'page', 'pages',
  'account', 'accounts', 'creator', 'creators', 'fan', 'fans', 'and', 'the', 'of', 'a',
  // Creator types, not niches: "Clipper" must not match "Car Editors — Clipper".
  'clipper', 'clippers', 'micro', 'ugc', 'influencer', 'influencers',
]);

/** Team niche labels can end in a creator type ("Movie Editors — Clipper"); drop it. */
function withoutCreatorType(label: string): string {
  return label.replace(/\s+[—–-]\s+(clipper|micro ugc|influencer)s?\s*$/i, '');
}

function tokens(s: string): string[] {
  const all = s
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((t) => (t.length > 3 && t.endsWith('s') && !t.endsWith('ss') ? t.slice(0, -1) : t));
  const meaningful = all.filter((t) => !STOPWORDS.has(t));
  return meaningful.length > 0 ? meaningful : all;
}

function bigrams(s: string): Map<string, number> {
  const out = new Map<string, number>();
  for (let i = 0; i < s.length - 1; i++) {
    const g = s.slice(i, i + 2);
    out.set(g, (out.get(g) ?? 0) + 1);
  }
  return out;
}

/** Sørensen–Dice similarity of two strings' letter pairs, 0..1. */
export function dice(a: string, b: string): number {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const A = bigrams(a);
  const B = bigrams(b);
  let overlap = 0;
  for (const [g, n] of A) overlap += Math.min(n, B.get(g) ?? 0);
  return (2 * overlap) / (a.length - 1 + (b.length - 1));
}

/** How well one file niche fits one team niche, 0..1. */
export function nicheScore(text: string, niche: NicheOption): number {
  const t = tokens(text);
  if (t.length === 0) return 0;
  // "Clipper", "Content Creator": a creator type, not a niche.
  if (t.every((x) => STOPWORDS.has(x) && !['edit', 'edits', 'editor'].includes(x))) return 0;
  const tc = t.join('');
  let best = 0;
  for (const label of [withoutCreatorType(niche.displayName), niche.code]) {
    const n = tokens(label);
    if (n.length === 0) continue;
    const nc = n.join('');
    if (tc === nc) return 1;
    const tSet = new Set(t);
    const nSet = new Set(n);
    if (n.every((x) => tSet.has(x)) || t.every((x) => nSet.has(x))) best = Math.max(best, 0.9);
    best = Math.max(best, dice(tc, nc));
  }
  return best;
}

export const NICHE_MATCH_THRESHOLD = 0.75;

/** The team niche a file value most likely means, or null when nothing is close. */
export function matchNiche(text: string, niches: ReadonlyArray<NicheOption>): string | null {
  if (isEmptyAnswer(text)) return null;
  let best: { code: string; score: number } | null = null;
  for (const n of niches) {
    const s = nicheScore(text, n);
    if (s >= NICHE_MATCH_THRESHOLD && (!best || s > best.score)) best = { code: n.code, score: s };
  }
  return best?.code ?? null;
}

/** Lookup key for a free-text niche: trimmed, case-folded. */
export function nicheKey(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

export interface DistinctNiche {
  key: string;
  /** The first spelling seen in the file. */
  label: string;
  count: number;
}

/** Every distinct niche value in the file, most common first. */
export function distinctNiches(values: ReadonlyArray<string>): DistinctNiche[] {
  const map = new Map<string, DistinctNiche>();
  for (const v of values) {
    if (isEmptyAnswer(v)) continue;
    const key = nicheKey(v);
    const hit = map.get(key);
    if (hit) hit.count += 1;
    else map.set(key, { key, label: v.trim(), count: 1 });
  }
  return [...map.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/** Auto-match every distinct value. Keys are {@link nicheKey}s; null means "None". */
export function autoMapNiches(
  distinct: ReadonlyArray<DistinctNiche>,
  niches: ReadonlyArray<NicheOption>,
): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const d of distinct) out[d.key] = matchNiche(d.label, niches);
  return out;
}
